import type { PPAGetScenarioViewResponse, PPAGetViewMetadataResponse } from 'types/ppaViews';

import type {
    LiveViewConfig,
    ScenarioViewData,
    ScenarioViewLens,
    ScenarioViewSeedCreated,
} from './scenarioLens.types';

export const mapPpaViewMetadataToScenarioViewData = (
    metadata: PPAGetViewMetadataResponse,
): ScenarioViewData => {
    const activeLensId =
        metadata.active_lens_id ??
        metadata.lenses.find((lens) => lens.is_default_lens)?.lens_id ??
        metadata.lenses.find((lens) => lens.is_selected)?.lens_id ??
        metadata.lenses[0]?.lens_id ??
        null;

    return {
        view_id: metadata.view_id,
        view_name: metadata.view_name,
        view_type: metadata.view_type,
        created_by: metadata.created_by,
        is_default: metadata.is_default,
        updated_at: metadata.updated_at,
        scope: metadata.filters,
        group_by: metadata.group_by.map((item) => ({
            attribute_id: 0,
            attribute_key: item.attribute_key,
            display_name: item.display_name,
            display_order: item.display_order,
        })),
        lenses: metadata.lenses.map((lens) => ({
            lens_id: lens.lens_id,
            lens_name: lens.lens_name,
            is_private: Boolean(lens.is_private),
            is_selected: lens.is_selected,
            display_order: lens.display_order,
            is_active_lens: activeLensId != null && lens.lens_id === activeLensId,
            is_default_lens: lens.is_default_lens,
        })),
        column_visibility: metadata.column_visibility?.map((item) => ({
            lens_id: item.lens_id,
            attribute_key: item.attribute_key,
            item_type: item.item_type,
            is_display_enabled: item.is_display_enabled,
        })),
        pending_messages: metadata.pending_messages ?? [],
    };
};

export const mapPpaScenarioViewToScenarioViewData = (
    scenarioView: PPAGetScenarioViewResponse,
): ScenarioViewData => {
    const activeLensId =
        scenarioView.active_lens_id ??
        scenarioView.lenses.find((lens) => lens.is_selected)?.lens_id ??
        scenarioView.lenses[0]?.lens_id ??
        null;

    return {
        view_id: scenarioView.base_view_id,
        view_name: scenarioView.has_deviation ? 'Scenario View' : scenarioView.base_view_name,
        view_type: scenarioView.has_deviation ? 'SCENARIO' : 'SYSTEM',
        created_by: null,
        is_default: false,
        scope: scenarioView.filters,
        group_by: scenarioView.group_by.map((item) => ({
            attribute_id: 0,
            attribute_key: item.attribute_key,
            display_name: item.display_name,
            display_order: item.display_order,
        })),
        lenses: scenarioView.lenses.map((lens) => ({
            lens_id: lens.lens_id,
            lens_name: lens.lens_name,
            is_private: lens.lens_type === 'CUSTOM',
            is_selected: lens.is_selected,
            display_order: lens.display_order,
            is_active_lens: activeLensId != null && lens.lens_id === activeLensId,
        })),
        column_visibility: scenarioView.column_visibility?.map((item) => ({
            lens_id: item.lens_id,
            attribute_key: item.attribute_key,
            item_type: item.item_type,
            is_display_enabled: item.is_display_enabled,
        })),
    };
};

/** Build metadata cache seed from live workspace config (matches create payload source). */
export const buildScenarioViewSeedFromLiveConfig = (
    base: ScenarioViewData,
    live: LiveViewConfig,
    created: ScenarioViewSeedCreated,
): ScenarioViewData => {
    const lensConfigById = new Map(live.lens_config.map((item) => [item.lens_id, item]));
    const groupByOrderByKey = new Map(
        live.group_by.map((item) => [item.attribute_key, item.display_order]),
    );

    return {
        ...base,
        view_id: created.viewId,
        view_name: created.viewName,
        is_default: created.isDefault,
        updated_at: null,
        scope: created.filters ?? base.scope,
        pending_messages: [],
        column_visibility: live.column_visibility,
        group_by: base.group_by
            .filter((item) => groupByOrderByKey.has(item.attribute_key))
            .map((item) => ({
                ...item,
                display_order: groupByOrderByKey.get(item.attribute_key) ?? item.display_order,
            }))
            .sort((left, right) => left.display_order - right.display_order),
        lenses: base.lenses.map((lens) => {
            const config = lensConfigById.get(lens.lens_id);
            if (!config) {
                return lens;
            }

            return {
                ...lens,
                is_selected: config.is_selected,
                display_order: config.display_order,
                is_active_lens: live.active_lens_id === lens.lens_id,
                is_default_lens: config.is_default_lens,
            };
        }),
    };
};

export const cloneScenarioView = (view: ScenarioViewData): ScenarioViewData =>
    JSON.parse(JSON.stringify(view)) as ScenarioViewData;

const normalizeScenarioViewLens = (lens: ScenarioViewLens & { is_custom?: boolean }): ScenarioViewLens => ({
    ...lens,
    is_private: Boolean(lens.is_private ?? lens.is_custom),
});

export const normalizeScenarioView = (view: ScenarioViewData): ScenarioViewData => ({
    ...view,
    lenses: view.lenses.map((lens) => normalizeScenarioViewLens(lens)),
});
